import React from "react"
import PropTypes from "prop-types"
import { Field, reduxForm } from "redux-form"
import { defineMessages, injectIntl } from "react-intl"
import { useSelector } from "react-redux"
// Material Ui
import {
  Box,
  Button,
  Card,
  CardContent,
  FormGroup,
  CardHeader
} from "@material-ui/core"
// Icons
import LockOpenIcon from "@material-ui/icons/LockOpen"
// ## //
import { Alert, AlertTitle } from "@material-ui/lab"
import FieldTextarea from "../../component/forms/FieldTextarea"
import ButtonSubmit from "../../component/buttons/ButtonSubmit"
import { words } from "../../i18n/lstrings"
import useSmartFormat from "../../i18n/useSmartFormat"
import useLoginStyles from "./loginStyle"
import FieldPassword from "../../component/forms/FieldPassword"

const messages = defineMessages({
  title: {
    id: "login.form.heading.login",
    defaultMessage: "Log in",
    description: "login form heading"
  },
  password: {
    id: "login.form.password",
    description: "login form password",
    defaultMessage: "Password"
  },
  login: {
    id: "login.form.login",
    defaultMessage: "Log in"
  },
  forgotPwdButton: {
    id: "login.form.forgotpassword",
    defaultMessage: "I forgot my password",
    description: "forgot password link"
  },
  remainingTries: {
    id: "login.form.remainingTries",
    defaultMessage:
      "After several failed attempts, your account will be disabled ({remainingTries} remaining)"
  },
  disabledAccount: {
    id: "login.form.disabledAccount",
    defaultMessage:
      "Your account is disabled. Please request a new password or contact an administrator."
  },
  invalidCredentials: {
    id: "action.errors.badcredentials",
    defaultMessage: "Your credentials are invalid"
  }
})

function getConnectionWarningMessage(remainingTries) {
  if (remainingTries === undefined) {
    return undefined
  }

  if (remainingTries > 0) {
    return { message: messages.remainingTries, level: "warning" }
  }

  return { message: messages.disabledAccount, level: "error" }
}

export const LoginForm = ({
  handleSubmit,
  submitting,
  handleSave,
  handleForgotPassword
}) => {
  const remainingTries = useSelector((state) => state.auth.remainingTries)
  const format = useSmartFormat()
  const classes = useLoginStyles()
  const connectionInfo = getConnectionWarningMessage(remainingTries)

  return (
    <Card className={classes.loginForm}>
      <CardHeader title={format(messages.title)} />
      <CardContent>
        <form onSubmit={handleSubmit(handleSave)}>
          <FormGroup>
            {connectionInfo && (
              <Alert severity={connectionInfo.level}>
                <AlertTitle>{format(messages.invalidCredentials)}</AlertTitle>
                {format(connectionInfo.message, { remainingTries })}
              </Alert>
            )}

            <Field
              component={FieldTextarea}
              label={format(words.email)}
              name="email"
              placeholder={format(words.email)}
              type="text"
              autoComplete="username"
            />

            <Field
              component={FieldPassword}
              label={format(messages.password)}
              name="password"
            />
            <Box className={classes.loginFormButton}>
              <Button
                size="small"
                variant="outlined"
                color="secondary"
                startIcon={<LockOpenIcon />}
                onClick={handleForgotPassword}
              >
                {format(messages.forgotPwdButton)}
              </Button>
              <ButtonSubmit
                size="small"
                disabled={submitting}
                text={messages.login}
              />
            </Box>
          </FormGroup>
        </form>
      </CardContent>
    </Card>
  )
}

export const validate = (values, props) => {
  const { formatMessage: format } = props.intl
  const errors = {}

  if (!values.email) {
    errors.email = format(words.required)
  }

  if (!values.password) {
    errors.password = format(words.required)
  }

  return errors
}

LoginForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  pristine: PropTypes.bool.isRequired,
  reset: PropTypes.func.isRequired,
  submitting: PropTypes.bool.isRequired,
  handleSave: PropTypes.func.isRequired,
  handleForgotPassword: PropTypes.func.isRequired,
  intl: PropTypes.any.isRequired,
  history: PropTypes.object
}
LoginForm.defaultProps = {
  history: {
    push: () => {
      // a basic noop function
    }
  }
}

export default injectIntl(
  reduxForm({
    form: "Login",
    validate
  })(LoginForm)
)
